import React, { Suspense, useContext, useState } from 'react';
import { LoadingSpinner } from '@uhg-eis/crimsonai-react-components';
import { Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Logout from '../Logout/Logout';
import Unauthorized from '../Unauthorized/Unauthorized';

import PrivacyPolicy from '../PrivacyPolicy/PrivacyPolicy';
import TermsOfuse from '../TermsOfUse/TermsOfUse';
import SplunkOtelWeb from '@splunk/otel-web';
import config from '../../constants/config.js';
import { AppHeader, SessionTimer } from '@uhg-eis/crimsonai-react-components';
import UserContext from '../../library/UserContext';
import Notification from '../Common/Notification';
import { StickyFooter } from '../Common/StickyFooter/StickyFooter';
import { StickyFooterProvider, useStickyFooter } from '../Common/StickyFooter/StickyFooterContext';
import { UserInfo } from '../../library/model/UserInfo';
import { isHPGClientByPermissions, replaceUrlDomain, shouldShowHPGLogoForRoute } from '../../constants/HPGConfig';
import Cookies from 'js-cookie';
import AppBusinessKey from '../../constants/app-mapping';
import { handleClientDomainFiltering } from '../../library/config';

// Disable splunk rum when running the app locally
// Guarded so a missing/invalid RUM token (e.g. env secret not configured) can't crash the whole bundle at load time.
if (process.env.REACT_APP_RUN_LOCALLY !== 'true' && process.env.REACT_APP_RUM_TOKEN) {
  try {
  SplunkOtelWeb.init({
    realm: config.splunk_realm,
    rumAccessToken: process.env.REACT_APP_RUM_TOKEN,
    applicationName: config.rum_product_name,
    version: config.version,
    deploymentEnvironment: process.env.REACT_APP_ENV,
  });
  } catch (err) {
    console.error('Failed to initialize Splunk RUM:', err);
  }
}

const Home = React.lazy(() => import('../Home/Home.component'));
const FooterComponent = React.lazy(() => import('../Footer/Footer'));
const Auth = React.lazy(() => import('../Auth/Auth.component'));
const UserList = React.lazy(() => import('../UserList/UserList'));

const PowerBi = React.lazy(() => import('../PowerBi/PowerBi'));
const Dashboard = React.lazy(() => import('../Dashboard/Dashboard'));
const GeneralSettingsLanding = React.lazy(() => import('../GeneralSettings/GeneralSettingsLanding'));
const AdhocReportList = React.lazy(() => import('../AdhocReports/AdhocReportList'));
const AdvancedReporting = React.lazy(() => import('../AdhocReports/AdvancedReporting/AdvancedReporting'));
const AdvancedReportingv2 = React.lazy(() => import('../AdhocReports/AdvancedReporting/V2/AdvancedReporting'));
const CreateReport = React.lazy(() => import('../AdhocReports/AdvancedReporting/V2/AdhocTemplates/AdhocTemplates'));

const App = () => {
  const { buttonList } = useStickyFooter();
  const [allowResetTimer, setAllowResetTimer] = useState<number>();
  const resetSessionTimer = () => {
    setAllowResetTimer(Math.random());
  };
  const location = useLocation();
  const noAppContentRoutes = ['/privacy-policy', '/terms-of-use'];
  const showAppContent = !noAppContentRoutes?.includes(location.pathname);
  let userContext = useContext(UserContext) as any;
  const { logout, refreshToken, guardianData, isGuardianFetching, getValidRolesFromAccounts} = userContext;
  const currentUser: UserInfo | undefined = userContext.getUserInfo();

  // OneLogin is ready once the /OneLoginAssociations response is available in context.
  const oneLoginReady = !!guardianData && guardianData.status !== 'failure';
  const appHeaderLoading = !oneLoginReady && !!isGuardianFetching;
  // Determine if HPG logo should be shown based on current route and permissions
  const showHPGLogo = shouldShowHPGLogoForRoute(location.pathname, Cookies.get('member_id'));

  const validRolesData = oneLoginReady ? getValidRolesFromAccounts(guardianData) : undefined;
  const clientFiltered = validRolesData ? handleClientDomainFiltering(validRolesData.validRoles) : undefined;

  let filteredRoles;
  if (oneLoginReady && validRolesData && clientFiltered) {
    const hasFilteredPermissions = clientFiltered.type === 'filter' && clientFiltered.filteredPermissions;
    filteredRoles = hasFilteredPermissions
      ? { validRoles: clientFiltered.filteredPermissions, validRoleType: validRolesData.validRoleType }
      : validRolesData;
  }

  return (
    <StickyFooterProvider>
      <GlobalNotifications />
      {!filteredRoles ? (
        <AppHeader
          isLoading={appHeaderLoading}
          handleDrawerToggle={() => {}}
          userInfo={{
            fullName: currentUser?.fullName,
            roles: [],
          }}
          moduleInfo={{
            moduleName: '',
            moduleVersion: config.version,
          }}
          showHLGLogo={showHPGLogo}
          logout={logout}
          userGuideUrl={replaceUrlDomain(process.env.REACT_APP_USER_GUIDE_URL || '')}
        />
      ) : (
        <AppHeader
          isLoading={appHeaderLoading}
          handleDrawerToggle={() => {}}
          userInfo={{
            fullName: currentUser?.fullName,
            roleType: filteredRoles.validRoleType,
            multiRoles: filteredRoles.validRoles,
          }}
          moduleInfo={{
            moduleName: '',
            moduleVersion: config.version,
            moduleNameMap: AppBusinessKey[(process.env.REACT_APP_ENV || 'dev').toLowerCase()] || {},
          }}
          showHLGLogo={showHPGLogo}
          logout={logout}
          userGuideUrl={replaceUrlDomain(process.env.REACT_APP_USER_GUIDE_URL || '')}
        />
      )}
      
      <SessionTimer
        logout={logout}
        refreshToken={refreshToken}
        checkMemberId={false}
        showIndicator={localStorage.getItem('debug_sessionTimer') === 'true'}
      />
      <Suspense fallback={<LoadingSpinner size={'l'} loading={true} loadingText='Loading, please wait...' />}>
        {showAppContent && (
          <div className={`app-content ${buttonList ? 'pb-0' : ''}`}>
            <Routes>
              <Route path='/auth' element={<Auth />} />
              <Route path='/' element={<Home />} />
              <Route path='/logout' element={<Logout />} />
              <Route path='/dashboard' element={<Dashboard />} />
              <Route path='/userlist' element={<UserList />} />
              <Route path='/general-settings' element={<GeneralSettingsLanding />} />
              <Route path='/reports' element={<PowerBi resetSessionTimer={resetSessionTimer} />} />
              <Route path='/adhoc-reports' element={<AdhocReportList />} />
              <Route path='/v2/advanced-reporting/*' element={<AdvancedReportingv2 />} />
              <Route path='/v2/advanced-reporting/create-report/*' element={<CreateReport />} />
              <Route path='/shared-reports' element={<AdvancedReporting isSharedReports={true} />}>
                <Route path='/shared-reports/:chosenFolder' element={<AdvancedReporting isSharedReports={true} />} />
                <Route
                  path='/shared-reports/:chosenFolder/:chosenSubfolder'
                  element={<AdvancedReporting isSharedReports={true} />}
                />
              </Route>
              <Route path='/my-reports' element={<AdvancedReporting />}>
                <Route path='/my-reports/:chosenFolder' element={<AdvancedReporting />} />
                <Route path='/my-reports/:chosenFolder/:chosenSubfolder' element={<AdvancedReporting />} />
              </Route>

              <Route path='/unauthorized' element={<Unauthorized />} />
            </Routes>
          </div>
        )}
        {!showAppContent && (
          <Routes>
            <Route path='/privacy-policy' element={<PrivacyPolicy />} />
            <Route path='/terms-of-use' element={<TermsOfuse />} />
          </Routes>
        )}
        <GlobalStickyFooter />
        {showAppContent && <FooterComponent />}
      </Suspense>
    </StickyFooterProvider>
  );
};

function GlobalStickyFooter() {
  const { buttonList, footerContent } = useStickyFooter();
  if (!buttonList && !footerContent) return null;
  return <StickyFooter buttonList={buttonList || []} footerContent={footerContent} />;
}

function GlobalNotifications() {
  const { isGuardianFetching, showErrorMsg, setShowErrorMsg, showInfo, setShowInfo } = useContext(UserContext);
  const location = useLocation();

  return (
    <>
      {showErrorMsg && (
        <Notification
          openState={showErrorMsg}
          onCloseCallback={() => setShowErrorMsg(false)}
          message={
            <>
              Unable to connect to the server. Please check your internet and log in again to continue.
              {' '}
              <a href={process.env.PUBLIC_URL} rel="noopener noreferrer" style={{ color: '#367BF5', textDecoration: 'underline', cursor: 'pointer' }}>
                [Re-login]
              </a>
            </>
          }
          alertSeverity="warning"
          autoHideDuration={null}
        />
      )}
      {showInfo && (
        <Notification
          openState={showInfo}
          onCloseCallback={() => setShowInfo(false)}
          message="No Association found!"
          alertSeverity="info"
          autoHideDuration={null}
        />
      )}
    </>
  );
}

export default App;
